import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Servicio global de confirmacion — reemplazo drop-in de window.confirm()
 * con un AlertDialog brandeado (shadcn).
 *
 * Uso desde cualquier parte del codigo (sin hooks, sin imports React):
 *   import { confirmAsync } from '@/lib/confirm';
 *   const ok = await confirmAsync('Borrar el cliente?');
 *   if (!ok) return;
 *
 * O con opciones extendidas:
 *   const ok = await confirmAsync({
 *     title: 'Borrar cliente',
 *     description: 'Esta accion no se puede deshacer.',
 *     confirmText: 'Borrar',
 *     destructive: true,
 *   });
 *
 * Montaje unico en App.js (o index.js):
 *   import { ConfirmDialogRoot } from '@/lib/confirm';
 *   ...
 *   <ConfirmDialogRoot />
 */

let currentResolver = null;
let currentState = {
  open: false,
  title: 'Confirmar',
  description: '',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  destructive: false,
};
const listeners = new Set();

function emit() {
  listeners.forEach((cb) => cb(currentState));
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Abre el dialogo y devuelve Promise<boolean> con el resultado.
 * Acepta string (compat con window.confirm) o un objeto con opciones.
 */
export function confirmAsync(optionsOrMessage) {
  const opts = typeof optionsOrMessage === 'string'
    ? { description: optionsOrMessage }
    : (optionsOrMessage || {});
  return new Promise((resolve) => {
    currentResolver = resolve;
    currentState = {
      open: true,
      title: opts.title || 'Confirmar',
      description: opts.description || '',
      confirmText: opts.confirmText || 'Confirmar',
      cancelText: opts.cancelText || 'Cancelar',
      destructive: !!opts.destructive,
    };
    emit();
  });
}

function resolveAndClose(result) {
  currentState = { ...currentState, open: false };
  const r = currentResolver;
  currentResolver = null;
  emit();
  r?.(result);
}

/**
 * Componente raiz. Montalo UNA SOLA VEZ en App.js (ej. dentro del router).
 * Se re-renderiza con cada confirmAsync().
 */
export function ConfirmDialogRoot() {
  const [s, setS] = useState(currentState);
  useEffect(() => subscribe(setS), []);

  return (
    <AlertDialog open={s.open} onOpenChange={(o) => !o && resolveAndClose(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{s.title}</AlertDialogTitle>
          {s.description && (
            <AlertDialogDescription className="whitespace-pre-line">
              {s.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => resolveAndClose(false)}>
            {s.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => resolveAndClose(true)}
            className={s.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
          >
            {s.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
